import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { listAudit, type AuditEntry } from "@/api/audit";
import { listRisks, type RiskIndicator } from "@/api/risks";
import { useSession } from "@/store/session";
import {
  archiveProject,
  deleteProject,
  getProject,
  listProjects,
  unarchiveProject,
  listProjectDependencies,
  listProjectTasks,
  patchProject,
  patchTask,
  simulateSetTaskOvertime,
  simulateTaskMove,
  type ApprovalRecord,
  type ListProjectsParams,
  type Project,
  type ProjectListItem,
  type ProjectPatch,
  type SetTaskOvertimeRequest,
  type Task,
  type TaskDependency,
  type TaskMoveRequest,
  type TaskPatch,
  type TaskWithCritical,
} from "@/api/projects";

export const PROJECTS_KEY = ["projects"] as const;
export const PROJECTS_PORTFOLIO_PARAMS = { limit: 200 } as const satisfies ListProjectsParams;
export const PROJECTS_PORTFOLIO_KEY = ["projects-portfolio", PROJECTS_PORTFOLIO_PARAMS] as const;
const FIELD_PROJECT_SEARCH_KEY = ["field-project-search"] as const;
export const PROJECT_KEY = (id: number) => ["project", id] as const;
export const PROJECT_TASKS_KEY = (id: number) => ["project-tasks", id] as const;
export const PROJECT_DEPS_KEY = (id: number) => ["project-deps", id] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useProjects(params: ListProjectsParams = {}) {
  return useQuery<ProjectListItem[], Error>({
    queryKey: [...PROJECTS_KEY, params] as const,
    queryFn: () => listProjects(params),
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useProject(projectId: number) {
  return useQuery<ProjectListItem, Error>({
    queryKey: PROJECT_KEY(projectId),
    queryFn: () => getProject(projectId),
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useProjectTasks(projectId: number) {
  return useQuery<TaskWithCritical[], Error>({
    queryKey: PROJECT_TASKS_KEY(projectId),
    queryFn: () => listProjectTasks(projectId),
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useProjectDependencies(projectId: number) {
  return useQuery<TaskDependency[], Error>({
    queryKey: PROJECT_DEPS_KEY(projectId),
    queryFn: () => listProjectDependencies(projectId),
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export interface PatchTaskVars {
  taskId: number;
  projectId: number;
  body: TaskPatch;
}

export function usePatchTask() {
  const qc = useQueryClient();
  return useMutation<Task, Error, PatchTaskVars>({
    mutationFn: ({ taskId, body }) => patchTask(taskId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PROJECT_TASKS_KEY(vars.projectId) });
      void qc.invalidateQueries({ queryKey: PROJECT_KEY(vars.projectId) });
    },
  });
}

export function useSimulateTaskMove() {
  const qc = useQueryClient();
  return useMutation<ApprovalRecord, Error, TaskMoveRequest>({
    mutationFn: simulateTaskMove,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approvals", "pending", "summary"] });
    },
  });
}

export const PROJECT_RISKS_KEY = (id: number) => ["project-risks", id] as const;
export const PROJECT_AUDIT_KEY = (id: number) => ["project-audit", id] as const;

function projectScopedKeys(projectId: number): QueryKey[] {
  return [
    PROJECT_KEY(projectId),
    PROJECT_TASKS_KEY(projectId),
    PROJECT_DEPS_KEY(projectId),
    PROJECT_RISKS_KEY(projectId),
    PROJECT_AUDIT_KEY(projectId),
    ["project-assembly-elements", projectId],
    ["project-metric-values", projectId],
    ["field", "assembly-elements", projectId],
    ["field-work-status", projectId],
  ];
}

function matchesProjectList(project: ProjectListItem, params: ListProjectsParams): boolean {
  if (params.archivedOnly) {
    if (!project.is_archived) return false;
  } else if (!params.includeArchived && project.is_archived) {
    return false;
  }
  return !params.status || project.status === params.status;
}

function byProjectId(left: ProjectListItem, right: ProjectListItem): number {
  return (left.id ?? Number.MAX_SAFE_INTEGER) - (right.id ?? Number.MAX_SAFE_INTEGER);
}

function reconcileProjectList(
  current: ProjectListItem[] | undefined,
  params: ListProjectsParams,
  projectId: number,
  project: ProjectListItem | undefined,
): ProjectListItem[] | undefined {
  if (current === undefined) return undefined;
  const hadProject = current.some((item) => item.id === projectId);
  const next = current.filter((item) => item.id !== projectId);
  const canInsert = hadProject || (params.offset ?? 0) === 0;
  if (project && canInsert && matchesProjectList(project, params)) next.push(project);
  next.sort(byProjectId);
  return params.limit === undefined ? next : next.slice(0, params.limit);
}

function reconcileProjectCaches(
  client: QueryClient,
  projectId: number,
  project: ProjectListItem | undefined,
): void {
  for (const query of client.getQueryCache().findAll({ queryKey: PROJECTS_KEY })) {
    const candidate = query.queryKey[1];
    const params =
      candidate !== null && typeof candidate === "object" ? (candidate as ListProjectsParams) : {};
    client.setQueryData<ProjectListItem[]>(query.queryKey, (current) =>
      reconcileProjectList(current, params, projectId, project),
    );
  }
  client.setQueryData<ProjectListItem[]>(PROJECTS_PORTFOLIO_KEY, (current) =>
    reconcileProjectList(current, PROJECTS_PORTFOLIO_PARAMS, projectId, project),
  );
}

async function settleProjectLifecycleCaches(
  client: QueryClient,
  projectId: number,
  project?: ProjectListItem,
): Promise<void> {
  const scopedKeys = projectScopedKeys(projectId);
  await Promise.all([
    client.cancelQueries({ queryKey: PROJECTS_KEY }),
    client.cancelQueries({ queryKey: PROJECTS_PORTFOLIO_KEY }),
    ...scopedKeys.map((queryKey) => client.cancelQueries({ queryKey })),
  ]);
  reconcileProjectCaches(client, projectId, project);
  for (const queryKey of scopedKeys) client.removeQueries({ queryKey });
  void client.invalidateQueries({ queryKey: PROJECTS_KEY });
  void client.invalidateQueries({ queryKey: PROJECTS_PORTFOLIO_KEY });
}

async function settleDeletedProjectCaches(client: QueryClient, projectId: number): Promise<void> {
  await client.cancelQueries({ queryKey: FIELD_PROJECT_SEARCH_KEY });
  client.removeQueries({ queryKey: FIELD_PROJECT_SEARCH_KEY });
  await settleProjectLifecycleCaches(client, projectId);
}

export function useProjectRisks(projectId: number, includeResolved = false) {
  return useQuery<RiskIndicator[], Error>({
    queryKey: [...PROJECT_RISKS_KEY(projectId), { includeResolved }] as const,
    queryFn: async () => {
      const all = await listRisks({ includeResolved });
      return all.filter((r) => r.project_id === projectId);
    },
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useProjectAudit(projectId: number, limit = 50) {
  const isAdmin = useSession((state) => state.user?.role === "admin");
  return useQuery<AuditEntry[], Error>({
    queryKey: [...PROJECT_AUDIT_KEY(projectId), { limit }] as const,
    queryFn: () => listAudit({ project_id: projectId, limit }),
    enabled: isAdmin,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export interface UpdateProjectVars {
  projectId: number;
  body: ProjectPatch;
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation<Project, Error, UpdateProjectVars>({
    mutationFn: ({ projectId, body }) => patchProject(projectId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PROJECT_KEY(vars.projectId) });
      void qc.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation<ProjectListItem, Error, number>({
    mutationFn: (projectId) => archiveProject(projectId),
    onSuccess: (data, projectId) => settleProjectLifecycleCaches(qc, projectId, data),
  });
}

export function useUnarchiveProject() {
  const qc = useQueryClient();
  return useMutation<ProjectListItem, Error, number>({
    mutationFn: (projectId) => unarchiveProject(projectId),
    onSuccess: (data, projectId) => settleProjectLifecycleCaches(qc, projectId, data),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (projectId) => deleteProject(projectId),
    onSuccess: (_data, projectId) => settleDeletedProjectCaches(qc, projectId),
  });
}

export function useSimulateSetTaskOvertime() {
  const qc = useQueryClient();
  return useMutation<ApprovalRecord, Error, SetTaskOvertimeRequest>({
    mutationFn: simulateSetTaskOvertime,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approvals", "pending", "summary"] });
    },
  });
}
